import projectsData from './projects.json';

/**
 * How far the evidence actually goes for a given project.
 *
 * This is a ceiling on what the copy may claim, not a label for the reader.
 * Across the whole historic proposal archive, zero documents supported a
 * `measured` claim (see RESEARCH-projects-catalog.md §8) — proposals are written
 * before delivery, so they state intent. `delivered` means OpenProject closed the
 * project; it does NOT mean a result was measured. Never put a performance number
 * in `outcome` unless the status is `measured` and there is real before/after data.
 */
export type OutcomeStatus = 'proposed' | 'contracted' | 'delivered' | 'measured';

/**
 * Client delivery, or one of Davinci's own platforms.
 *
 * These are kept visibly distinct rather than blended. The catalogue's implicit
 * promise is "work we did for clients"; quietly mixing in-house product work into
 * that is the kind of thing a careful buyer notices, and noticing one devalues the
 * other thirty. Labelled honestly, the in-house platforms are among the strongest
 * entries — no client consent needed, nothing anonymised, and a prospect can be
 * shown the running system.
 */
export type Engagement = 'client' | 'in-house';

export const ENGAGEMENT_LABEL: Record<Engagement, string> = {
  client: 'Client work',
  'in-house': 'In-house platform',
};

export interface Project {
  slug: string;
  engagement: Engagement;
  /** OpenProject id — provenance for anyone tracing a claim back to its source. */
  openProjectId: number;
  title: string;
  cardSummary: string;
  serviceCategory: string;
  industry: string;
  /**
   * Anonymised size/shape descriptor. Keep to at most TWO narrowing attributes:
   * in a market this size, "a 200-employee Alberta roofing contractor" identifies
   * exactly one company. Broaden the narrowest attribute, never stack them.
   */
  sizeDescriptor: string;
  outcomeType: string;
  outcomeStatus: OutcomeStatus;
  year: string;
  /** The specific stack, shown on cards and detail pages. Credibility lives here. */
  technologies: string[];
  /**
   * Consolidated groups used for FILTERING. Deliberately a much smaller controlled
   * vocabulary than `technologies`: 33 specific products across nine projects left
   * 28 of them at a count of one, and a filter where every option returns a single
   * result is a worse table of contents than the grid. Add a project's real products
   * to `technologies`; map it to two to four existing `capabilities`. Projects
   * genuinely span several — a cloud migration is also networking, identity and
   * security work — and under-tagging makes the rail look emptier than the
   * catalogue actually is.
   */
  capabilities: string[];
  /** The four beats: challenge → guide → approach → outcome. Client owns the win. */
  challenge: string;
  guide: string;
  approach: string[];
  outcome: string[];
  /**
   * Optional: the service page that features this project. Overrides the category
   * mapping (PROJECT_SERVICE_LINKS) for the "Part of our ..." line, so a project
   * featured on /services/managed-it links back there.
   */
  serviceRoute?: string;
}

export const projects: Project[] = projectsData.projects as Project[];

const PROJECT_SLUG_ALIASES: Record<string, string> = {
  'davinci-erp': 'davinci-erp-platform',
  'compass-mcp-integration': 'model-context-protocol-erp-integration',
  'fleet-management-tool': 'fleet-data-intelligence-platform',
  'accounting-dashboard': 'kpi-scorecard-automation',
  'document-creator': 'multi-tenant-project-operations-platform',
  'code-pairing-graph-api-tool': 'cross-tenant-identity-synchronisation',
  'canadian-Hours-of-Service-Calculator': 'homebuilder-sales-operations-platform',
  'alberta-herbicide-calculator': 'estimating-tool-dynamics-integration',
  'restaurant-management-app': 'configurable-product-web-store',
  'boardwalk-alert-watcher': 'measuring-ai-reliability-before-improving-it',
};

export const getProject = (slug: string): Project | undefined =>
  projects.find((project) => project.slug === slug || project.slug === PROJECT_SLUG_ALIASES[slug]);

/**
 * Facet values are derived from the data rather than hand-listed, so a filter can
 * never advertise a category with nothing behind it. `Insights.tsx` carries the
 * same rule in a comment — empty filter tabs read as broken promises.
 */
const distinct = (values: string[]): string[] =>
  Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));

export const serviceCategories = distinct(projects.map((p) => p.serviceCategory));
export const industries = distinct(projects.map((p) => p.industry));
export const technologies = distinct(projects.flatMap((p) => p.technologies));
export const capabilities = distinct(projects.flatMap((p) => p.capabilities));

export type FacetKey = 'service' | 'industry' | 'tech' | 'outcome' | 'engagement';

/** Every value a project contributes to a given facet. */
const facetValues: Record<FacetKey, (project: Project) => string[]> = {
  service: (p) => [p.serviceCategory],
  industry: (p) => [p.industry],
  tech: (p) => p.capabilities,
  outcome: (p) => [p.outcomeType],
  // Returns the display label so the checkbox, the applied chip and the URL all
  // read in plain language while the stored value stays a clean flag.
  engagement: (p) => [ENGAGEMENT_LABEL[p.engagement]],
};

export interface FacetDefinition {
  key: FacetKey;
  /** Heading shown above the checkbox group; also its <legend>. */
  label: string;
  values: string[];
  /** Facets with many values collapse to the top few until "show all". */
  initiallyVisible: number;
}

/** Ordered most- to least-used, so the useful values sit above any "show all" fold. */
const byFrequencyThenName = (key: FacetKey) => (a: string, b: string) => {
  const count = (value: string) =>
    projects.filter((p) => facetValues[key](p).includes(value)).length;
  return count(b) - count(a) || a.localeCompare(b);
};

/**
 * Two facets only.
 *
 * Industry and outcome-type are deliberately NOT filters. Industry spreads nine
 * projects across six sectors, so most values would sit at a count of one — a
 * filter whose every option returns a single result is a worse table of contents
 * than the grid itself. Both remain visible on each card and in free-text search,
 * which is where thin dimensions belong: as information, not as controls.
 * Revisit if industry counts thicken as the catalogue grows toward 40+.
 */
export const FACETS: FacetDefinition[] = [
  { key: 'service', label: 'Type of work', values: serviceCategories, initiallyVisible: 12 },
  {
    key: 'engagement',
    label: 'Engagement',
    values: [ENGAGEMENT_LABEL.client, ENGAGEMENT_LABEL['in-house']],
    initiallyVisible: 12,
  },
  {
    key: 'tech',
    label: 'Technology',
    values: [...capabilities].sort(byFrequencyThenName('tech')),
    initiallyVisible: 12,
  },
];

export type Selection = Record<FacetKey, string[]>;

export const EMPTY_SELECTION: Selection = {
  service: [], industry: [], tech: [], outcome: [], engagement: [],
};

/**
 * Within a facet the selected values are OR'd; across facets they are AND'd.
 * That is what every e-commerce filter rail does, and it is what makes ticking a
 * second technology widen rather than empty the results.
 *
 * `ignore` omits one facet from the test, which is how the counts beside each
 * checkbox are produced — a count must reflect the OTHER facets' selections, or
 * the rail offers options that lead to zero results.
 */
export const matchesSelection = (
  project: Project,
  selection: Selection,
  ignore?: FacetKey,
): boolean =>
  (Object.keys(facetValues) as FacetKey[]).every((key) => {
    if (key === ignore) return true;
    const chosen = selection[key];
    if (!chosen.length) return true;
    const values = facetValues[key](project);
    return chosen.some((value) => values.includes(value));
  });

/** How many results each value of `key` would yield given the other facets. */
export const facetCounts = (
  key: FacetKey,
  selection: Selection,
  query: string,
): Record<string, number> => {
  const pool = projects.filter(
    (project) => matchesSelection(project, selection, key) && matchesSearch(project, query),
  );
  const counts: Record<string, number> = {};
  pool.forEach((project) => {
    facetValues[key](project).forEach((value) => {
      counts[value] = (counts[value] ?? 0) + 1;
    });
  });
  return counts;
};

/** Free-text haystack, built once per project rather than per keystroke. */
const searchIndex = new Map<string, string>(
  projects.map((project) => [
    project.slug,
    [
      project.title,
      project.cardSummary,
      project.serviceCategory,
      project.industry,
      project.sizeDescriptor,
      project.outcomeType,
      project.challenge,
      ...project.technologies,
      ...project.capabilities,
    ]
      .join(' ')
      .toLowerCase(),
  ]),
);

export const matchesSearch = (project: Project, query: string): boolean => {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;
  const haystack = searchIndex.get(project.slug) ?? '';
  return trimmed.split(/\s+/).every((term) => haystack.includes(term));
};
